import { create } from "zustand";

/** Chrome/Edge/Samsung Internet's install event (not in the TS DOM lib yet). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPlatform =
  | "installed" // already running as the installed app
  | "prompt" // Android/desktop Chromium: one-tap install available
  | "ios-safari" // iPhone/iPad Safari: Share, then Add to Home Screen
  | "ios-other" // iPhone/iPad in Chrome, Firefox or an in-app browser: must open in Safari first
  | "in-app" // Android in-app browser (Instagram, Facebook...): must open in Chrome first
  | "unsupported";

interface InstallState {
  deferred: BeforeInstallPromptEvent | null;
  installed: boolean;
}

export const useInstallStore = create<InstallState>(() => ({ deferred: null, installed: false }));

const VISITS_KEY = "rs:visits";
const SNOOZE_KEY = "rs:install-snoozed-until";
const SESSION_KEY = "rs:visit-counted";
export const SNOOZE_DAYS = 14;
/** The banner waits for a returning visitor: first-timers get the shop, not a pop-up. */
export const MIN_VISITS = 2;

function read(key: string, storage: Storage | undefined = safeLocal()): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string, storage: Storage | undefined = safeLocal()): void {
  try {
    storage?.setItem(key, value);
  } catch {
    /* private mode or storage blocked: the banner just shows again next time */
  }
}

function safeLocal(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function safeSession(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Counts one visit per browser session. */
export function countVisit(): number {
  const visits = Number(read(VISITS_KEY)) || 0;
  if (read(SESSION_KEY, safeSession())) return visits;
  write(SESSION_KEY, "1", safeSession());
  write(VISITS_KEY, String(visits + 1));
  return visits + 1;
}

export function visitCount(): number {
  return Number(read(VISITS_KEY)) || 0;
}

export function snoozeInstall(now = Date.now()): void {
  write(SNOOZE_KEY, String(now + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
}

export function isSnoozed(now = Date.now()): boolean {
  return Number(read(SNOOZE_KEY) ?? 0) > now;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

/** Works out how this browser installs apps, from its user agent. */
export function detectPlatform(ua: string, opts: { standalone: boolean; canPrompt: boolean; touchMac?: boolean }): InstallPlatform {
  if (opts.standalone) return "installed";
  const ios = /iPhone|iPad|iPod/i.test(ua) || !!opts.touchMac;
  const inApp = /FBAN|FBAV|Instagram|Line\/|Twitter|TikTok|Snapchat|; wv\)/i.test(ua);
  if (ios) {
    const otherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//i.test(ua) || inApp;
    return otherBrowser ? "ios-other" : "ios-safari";
  }
  if (opts.canPrompt) return "prompt";
  if (inApp && /Android/i.test(ua)) return "in-app";
  return "unsupported";
}

export function currentPlatform(canPrompt: boolean): InstallPlatform {
  if (typeof navigator === "undefined") return "unsupported";
  const touchMac = /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1; // iPadOS reports as a Mac
  return detectPlatform(navigator.userAgent, { standalone: isStandalone(), canPrompt, touchMac });
}

/**
 * Listen for the install events as early as possible (main.tsx): Chrome fires
 * `beforeinstallprompt` once, often before React has rendered anything.
 */
export function captureInstallPrompt(): void {
  if (typeof window === "undefined") return;
  countVisit();
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // we show our own banner instead of Chrome's mini-infobar
    useInstallStore.setState({ deferred: event as BeforeInstallPromptEvent });
  });
  window.addEventListener("appinstalled", () => {
    useInstallStore.setState({ deferred: null, installed: true });
  });
}

/** Opens Chrome's install dialog. Resolves true when the user accepts. */
export async function promptInstall(): Promise<boolean> {
  const { deferred } = useInstallStore.getState();
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  useInstallStore.setState({ deferred: null, installed: outcome === "accepted" });
  return outcome === "accepted";
}
